import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, Download, FileText, Layers, CheckCircle2 } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import LeadDialog from "@/components/LeadDialog";

const ReportDetail = () => {
  const { slug } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [granted, setGranted] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  useEffect(() => {
    api.get(`/reports/${slug}`).then((r) => setReport(r.data)).catch(() => setReport(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="mx-auto max-w-5xl px-5 py-16"><Skeleton className="h-96 rounded-xl" /></div>;
  if (!report) return (
    <div className="py-32 text-center" data-testid="report-not-found">
      <p className="text-muted-foreground">Report not found.</p>
      <Link to="/reports" className="text-electric font-medium">Back to reports</Link>
    </div>
  );

  return (
    <div data-testid="report-detail">
      <div className="bg-navy text-white">
        <div className="mx-auto max-w-5xl px-5 lg:px-8 py-14">
          <Link to="/reports" className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white mb-6" data-testid="back-to-reports">
            <ArrowLeft className="h-4 w-4" /> All reports
          </Link>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <Badge className="bg-electric text-white hover:bg-electric rounded-full">{report.report_type}</Badge>
              <h1 className="mt-4 font-display text-3xl lg:text-4xl font-extrabold leading-tight">{report.title}</h1>
              <p className="mt-3 text-white/75">{report.subtitle}</p>
              <div className="mt-5 flex items-center gap-5 text-sm text-white/60">
                <span className="flex items-center gap-1"><Layers className="h-4 w-4" /> {report.pages} pages</span>
                <span className="flex items-center gap-1"><FileText className="h-4 w-4" /> {report.category}</span>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-white/15">
              {report.cover_image && <img src={report.cover_image} alt={report.title} className="w-full h-64 object-cover" />}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-5 lg:px-8 py-14 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h2 className="font-display text-2xl font-extrabold text-navy">About this report</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed text-lg">{report.summary}</p>
          {report.highlights?.length > 0 && (
            <>
              <h3 className="mt-10 font-display text-xl font-bold text-navy">What's inside</h3>
              <ul className="mt-5 space-y-3">
                {report.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-electric shrink-0 mt-0.5" />
                    <span className="text-navy/80">{h}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-24 rounded-2xl border border-border bg-white p-7 shadow-sm" data-testid="report-gate">
            {granted ? (
              <div className="text-center" data-testid="report-granted">
                <CheckCircle2 className="h-10 w-10 text-electric mx-auto" />
                <p className="mt-3 font-display font-bold text-navy">Access granted</p>
                <p className="mt-1 text-sm text-muted-foreground">A copy is on its way to your inbox. You can also download the executive summary below.</p>
                <Button className="btn-electric rounded-full w-full mt-5" data-testid="report-download-now"
                  onClick={() => downloadUrl && window.open(fileUrl(downloadUrl), "_blank")}>
                  <Download className="h-4 w-4" /> Download PDF
                </Button>
              </div>
            ) : (
              <>
                <p className="overline-label text-electric">Gated Research</p>
                <h3 className="mt-2 font-display text-lg font-bold text-navy">Unlock the full report</h3>
                <p className="mt-2 text-sm text-muted-foreground">Share a few details to receive the complete {report.pages}-page report.</p>
                <LeadDialog
                  source="report"
                  reportSlug={report.slug}
                  title={`Download: ${report.title}`}
                  onGranted={(data) => { setGranted(true); setDownloadUrl(data?.download_url); if (data?.download_url) window.open(fileUrl(data.download_url), "_blank"); }}
                  trigger={<Button className="btn-electric rounded-full w-full mt-5" data-testid="report-unlock">Unlock Report</Button>}
                />
                <p className="mt-4 text-[11px] text-muted-foreground text-center">No spam. Enterprise-grade privacy.</p>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ReportDetail;
